import { addDays, subDays } from 'date-fns';
import type { AllocationStatus, Receipt, Transaction } from '@/types/payments';
import type { TransactionStatus } from '@/types/transaction';

function computeTransactionStatus(daysOverdue: number, currentBalance: number): TransactionStatus {
  if (currentBalance <= 0) return 'completed';
  if (daysOverdue > 0) return 'overdue';
  return 'outstanding';
}

function computeAllocationStatus(unappliedAmount: number, receivedAmount: number): AllocationStatus {
  if (unappliedAmount === 0) return 'fully_allocated';
  if (unappliedAmount >= receivedAmount) return 'unallocated';
  return 'partially_allocated';
}

// Mock Receipts Data - UK-centric with all GBP currency
export const generateMockReceipts = (): Receipt[] => {
  const receipts: Omit<Receipt, 'allocationStatus'>[] = [
    // TEST RECEIPT FOR PROMISE TO PAY (Phase 2)
    {
      id: 1,
      receiptNumber: 'RCP-2025-001',
      customerId: 1,
      customerName: 'ABC Company Ltd',
      customerAccountNumber: 'ACC-001',
      receiptDate: new Date('2025-01-15'),
      receivedAmount: 1250.0,
      allocatedAmount: 0,
      unappliedAmount: 1250.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BACS-12345',
      bankReference: 'REF-2025-001',
      externalId: 'EXT-001',
      importedAt: new Date('2025-01-15'),
      createdBy: 'system',
      createdAt: new Date('2025-01-15'),
      notes: 'Payment received via bank transfer - MATCHES PROMISE',
      hasPromise: true,
    },
    // MAIN TEST RECEIPT FOR ALLOCATION WORKSPACE
    {
      id: 101,
      receiptNumber: 'REC-001',
      customerId: 101,
      customerName: 'Acme Distribution Ltd',
      customerAccountNumber: 'ACC-1001',
      receiptDate: new Date('2025-11-10'),
      receivedAmount: 5000.0,
      allocatedAmount: 0,
      unappliedAmount: 5000.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'REF-884523',
      bankReference: 'REF-884523',
      externalId: 'EXT-002',
      importedAt: new Date('2025-11-10'),
      createdBy: 'system',
      createdAt: new Date('2025-11-10'),
      hasPromise: false,
    },
    {
      id: 102,
      receiptNumber: 'RCP-002',
      customerId: 102,
      customerName: 'Tech Solutions Inc',
      customerAccountNumber: 'ACC-002',
      receiptDate: new Date(),
      receivedAmount: 3200.0,
      allocatedAmount: 2000.0,
      unappliedAmount: 1200.0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-67890',
      externalId: 'EXT-003',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 103,
      receiptNumber: 'RCP-003',
      customerId: 103,
      customerName: 'Global Retail Shop Inc',
      customerAccountNumber: 'ACC-003',
      receiptDate: subDays(new Date(), 1),
      receivedAmount: 8500.0,
      allocatedAmount: 8500.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-54321',
      externalId: 'EXT-004',
      importedAt: subDays(new Date(), 1),
      createdBy: 'system',
      createdAt: subDays(new Date(), 1),
      hasPromise: false,
    },
    {
      id: 104,
      receiptNumber: 'RCP-004',
      customerId: 104,
      customerName: 'Manufacturing Co Ltd',
      customerAccountNumber: 'ACC-004',
      receiptDate: subDays(new Date(), 2),
      receivedAmount: 12500.0,
      allocatedAmount: 0,
      unappliedAmount: 12500.0,
      currency: 'GBP',
      paymentMethod: 'direct_debit',
      paymentReference: 'DD-11111',
      externalId: 'EXT-005',
      importedAt: subDays(new Date(), 2),
      createdBy: 'system',
      createdAt: subDays(new Date(), 2),
      hasPromise: false,
    },
    {
      id: 105,
      receiptNumber: 'RCP-005',
      customerId: 105,
      customerName: 'Services Group PLC',
      customerAccountNumber: 'ACC-005',
      receiptDate: subDays(new Date(), 3),
      receivedAmount: 7800.0,
      allocatedAmount: 5000.0,
      unappliedAmount: 2800.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-99999',
      externalId: 'EXT-006',
      importedAt: subDays(new Date(), 3),
      createdBy: 'system',
      createdAt: subDays(new Date(), 3),
      hasPromise: true,
    },
    {
      id: 106,
      receiptNumber: 'RCP-006',
      customerId: 106,
      customerName: 'Digital Media Ltd',
      customerAccountNumber: 'ACC-006',
      receiptDate: subDays(new Date(), 5),
      receivedAmount: 4500.0,
      allocatedAmount: 0,
      unappliedAmount: 4500.0,
      currency: 'GBP',
      paymentMethod: 'cheque',
      paymentReference: 'CHQ-12345',
      externalId: 'EXT-007',
      importedAt: subDays(new Date(), 5),
      createdBy: 'system',
      createdAt: subDays(new Date(), 5),
      hasPromise: false,
    },
    {
      id: 107,
      receiptNumber: 'RCP-007',
      customerId: 107,
      customerName: 'Construction Partners Ltd',
      customerAccountNumber: 'ACC-007',
      receiptDate: subDays(new Date(), 7),
      receivedAmount: 15000.0,
      allocatedAmount: 15000.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-77777',
      externalId: 'EXT-008',
      importedAt: subDays(new Date(), 7),
      createdBy: 'system',
      createdAt: subDays(new Date(), 7),
      hasPromise: false,
    },
    {
      id: 108,
      receiptNumber: 'RCP-008',
      customerId: 108,
      customerName: 'Healthcare Systems Inc',
      customerAccountNumber: 'ACC-008',
      receiptDate: subDays(new Date(), 10),
      receivedAmount: 9200.0,
      allocatedAmount: 3000.0,
      unappliedAmount: 6200.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-88888',
      externalId: 'EXT-009',
      importedAt: subDays(new Date(), 10),
      createdBy: 'system',
      createdAt: subDays(new Date(), 10),
      hasPromise: false,
    },
    // Additional unallocated receipts to meet threshold
    {
      id: 109,
      receiptNumber: 'RCP-009',
      customerId: 109,
      customerName: 'Retail Solutions Ltd',
      customerAccountNumber: 'ACC-009',
      receiptDate: subDays(new Date(), 4),
      receivedAmount: 3500.0,
      allocatedAmount: 0,
      unappliedAmount: 3500.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10001',
      externalId: 'EXT-010',
      importedAt: subDays(new Date(), 4),
      createdBy: 'system',
      createdAt: subDays(new Date(), 4),
      hasPromise: false,
    },
    {
      id: 110,
      receiptNumber: 'RCP-010',
      customerId: 110,
      customerName: 'Logistics Express Inc',
      customerAccountNumber: 'ACC-010',
      receiptDate: subDays(new Date(), 6),
      receivedAmount: 5200.0,
      allocatedAmount: 0,
      unappliedAmount: 5200.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10002',
      externalId: 'EXT-011',
      importedAt: subDays(new Date(), 6),
      createdBy: 'system',
      createdAt: subDays(new Date(), 6),
      hasPromise: false,
    },
    {
      id: 111,
      receiptNumber: 'RCP-011',
      customerId: 111,
      customerName: 'Professional Services Co',
      customerAccountNumber: 'ACC-011',
      receiptDate: subDays(new Date(), 8),
      receivedAmount: 6800.0,
      allocatedAmount: 0,
      unappliedAmount: 6800.0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-10003',
      externalId: 'EXT-012',
      importedAt: subDays(new Date(), 8),
      createdBy: 'system',
      createdAt: subDays(new Date(), 8),
      hasPromise: false,
    },
    {
      id: 112,
      receiptNumber: 'RCP-012',
      customerId: 112,
      customerName: 'Energy Solutions PLC',
      customerAccountNumber: 'ACC-012',
      receiptDate: subDays(new Date(), 9),
      receivedAmount: 4200.0,
      allocatedAmount: 0,
      unappliedAmount: 4200.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10004',
      externalId: 'EXT-013',
      importedAt: subDays(new Date(), 9),
      createdBy: 'system',
      createdAt: subDays(new Date(), 9),
      hasPromise: false,
    },
    {
      id: 113,
      receiptNumber: 'RCP-013',
      customerId: 113,
      customerName: 'Marketing Agency Ltd',
      customerAccountNumber: 'ACC-013',
      receiptDate: subDays(new Date(), 11),
      receivedAmount: 7500.0,
      allocatedAmount: 0,
      unappliedAmount: 7500.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10005',
      externalId: 'EXT-014',
      importedAt: subDays(new Date(), 11),
      createdBy: 'system',
      createdAt: subDays(new Date(), 11),
      hasPromise: false,
    },
    {
      id: 114,
      receiptNumber: 'RCP-014',
      customerId: 114,
      customerName: 'Financial Advisors Inc',
      customerAccountNumber: 'ACC-014',
      receiptDate: subDays(new Date(), 12),
      receivedAmount: 3900.0,
      allocatedAmount: 0,
      unappliedAmount: 3900.0,
      currency: 'GBP',
      paymentMethod: 'direct_debit',
      paymentReference: 'DD-10006',
      externalId: 'EXT-015',
      importedAt: subDays(new Date(), 12),
      createdBy: 'system',
      createdAt: subDays(new Date(), 12),
      hasPromise: false,
    },
    {
      id: 115,
      receiptNumber: 'RCP-015',
      customerId: 115,
      customerName: 'Transport Group Ltd',
      customerAccountNumber: 'ACC-015',
      receiptDate: subDays(new Date(), 13),
      receivedAmount: 8900.0,
      allocatedAmount: 0,
      unappliedAmount: 8900.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10007',
      externalId: 'EXT-016',
      importedAt: subDays(new Date(), 13),
      createdBy: 'system',
      createdAt: subDays(new Date(), 13),
      hasPromise: false,
    },
    {
      id: 116,
      receiptNumber: 'RCP-016',
      customerId: 116,
      customerName: 'Consulting Partners PLC',
      customerAccountNumber: 'ACC-016',
      receiptDate: subDays(new Date(), 14),
      receivedAmount: 5600.0,
      allocatedAmount: 0,
      unappliedAmount: 5600.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10008',
      externalId: 'EXT-017',
      importedAt: subDays(new Date(), 14),
      createdBy: 'system',
      createdAt: subDays(new Date(), 14),
      hasPromise: false,
    },
    {
      id: 117,
      receiptNumber: 'RCP-017',
      customerId: 117,
      customerName: 'Technology Ventures Ltd',
      customerAccountNumber: 'ACC-017',
      receiptDate: subDays(new Date(), 15),
      receivedAmount: 4700.0,
      allocatedAmount: 0,
      unappliedAmount: 4700.0,
      currency: 'GBP',
      paymentMethod: 'cheque',
      paymentReference: 'CHQ-10009',
      externalId: 'EXT-018',
      importedAt: subDays(new Date(), 15),
      createdBy: 'system',
      createdAt: subDays(new Date(), 15),
      hasPromise: false,
    },
    {
      id: 118,
      receiptNumber: 'RCP-018',
      customerId: 118,
      customerName: 'Property Management Co',
      customerAccountNumber: 'ACC-018',
      receiptDate: subDays(new Date(), 16),
      receivedAmount: 6300.0,
      allocatedAmount: 0,
      unappliedAmount: 6300.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10010',
      externalId: 'EXT-019',
      importedAt: subDays(new Date(), 16),
      createdBy: 'system',
      createdAt: subDays(new Date(), 16),
      hasPromise: false,
    },
    {
      id: 119,
      receiptNumber: 'RCP-019',
      customerId: 119,
      customerName: 'Insurance Brokers Ltd',
      customerAccountNumber: 'ACC-019',
      receiptDate: subDays(new Date(), 17),
      receivedAmount: 5400.0,
      allocatedAmount: 0,
      unappliedAmount: 5400.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10011',
      externalId: 'EXT-020',
      importedAt: subDays(new Date(), 17),
      createdBy: 'system',
      createdAt: subDays(new Date(), 17),
      hasPromise: false,
    },
    {
      id: 120,
      receiptNumber: 'RCP-020',
      customerId: 120,
      customerName: 'Education Services Inc',
      customerAccountNumber: 'ACC-020',
      receiptDate: subDays(new Date(), 18),
      receivedAmount: 7200.0,
      allocatedAmount: 0,
      unappliedAmount: 7200.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10012',
      externalId: 'EXT-021',
      importedAt: subDays(new Date(), 18),
      createdBy: 'system',
      createdAt: subDays(new Date(), 18),
      hasPromise: false,
    },
    {
      id: 121,
      receiptNumber: 'RCP-021',
      customerId: 121,
      customerName: 'Hospitality Group PLC',
      customerAccountNumber: 'ACC-021',
      receiptDate: subDays(new Date(), 19),
      receivedAmount: 4100.0,
      allocatedAmount: 0,
      unappliedAmount: 4100.0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-10013',
      externalId: 'EXT-022',
      importedAt: subDays(new Date(), 19),
      createdBy: 'system',
      createdAt: subDays(new Date(), 19),
      hasPromise: false,
    },
    {
      id: 122,
      receiptNumber: 'RCP-022',
      customerId: 122,
      customerName: 'Engineering Solutions Ltd',
      customerAccountNumber: 'ACC-022',
      receiptDate: subDays(new Date(), 20),
      receivedAmount: 8600.0,
      allocatedAmount: 0,
      unappliedAmount: 8600.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10014',
      externalId: 'EXT-023',
      importedAt: subDays(new Date(), 20),
      createdBy: 'system',
      createdAt: subDays(new Date(), 20),
      hasPromise: false,
    },
    {
      id: 123,
      receiptNumber: 'RCP-023',
      customerId: 123,
      customerName: 'Legal Services Co',
      customerAccountNumber: 'ACC-023',
      receiptDate: subDays(new Date(), 21),
      receivedAmount: 5900.0,
      allocatedAmount: 0,
      unappliedAmount: 5900.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10015',
      externalId: 'EXT-024',
      importedAt: subDays(new Date(), 21),
      createdBy: 'system',
      createdAt: subDays(new Date(), 21),
      hasPromise: false,
    },
    {
      id: 124,
      receiptNumber: 'RCP-024',
      customerId: 124,
      customerName: 'Pharmaceutical Distributors Ltd',
      customerAccountNumber: 'ACC-024',
      receiptDate: subDays(new Date(), 22),
      receivedAmount: 9800.0,
      allocatedAmount: 0,
      unappliedAmount: 9800.0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BANK-10016',
      externalId: 'EXT-025',
      importedAt: subDays(new Date(), 22),
      createdBy: 'system',
      createdAt: subDays(new Date(), 22),
      hasPromise: false,
    },
    {
      id: 125,
      receiptNumber: 'RCP-025',
      customerId: 125,
      customerName: 'Telecommunications Inc',
      customerAccountNumber: 'ACC-025',
      receiptDate: subDays(new Date(), 23),
      receivedAmount: 6700.0,
      allocatedAmount: 0,
      unappliedAmount: 6700.0,
      currency: 'GBP',
      paymentMethod: 'direct_debit',
      paymentReference: 'DD-10017',
      externalId: 'EXT-026',
      importedAt: subDays(new Date(), 23),
      createdBy: 'system',
      createdAt: subDays(new Date(), 23),
      hasPromise: false,
    },
    // Additional receipts received TODAY for dashboard metrics
    {
      id: 126,
      receiptNumber: 'RCP-026',
      customerId: 126,
      customerName: 'Digital Marketing Co',
      customerAccountNumber: 'ACC-026',
      receiptDate: new Date(),
      receivedAmount: 4500.0,
      allocatedAmount: 4500.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BT-20001',
      externalId: 'EXT-027',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    // NON-HOME CURRENCY RECEIPTS (USD and EUR)
    {
      id: 200,
      receiptNumber: 'RCP-USD-001',
      customerId: 200,
      customerName: 'American Tech Corp',
      customerAccountNumber: 'ACC-200',
      receiptDate: subDays(new Date(), 2),
      receivedAmount: 8500.0,
      allocatedAmount: 3000.0,
      unappliedAmount: 5500.0,
      currency: 'USD',
      paymentMethod: 'bank_transfer',
      paymentReference: 'WIRE-USD-001',
      bankReference: 'SWIFT-USD-001',
      externalId: 'EXT-200',
      importedAt: subDays(new Date(), 2),
      createdBy: 'system',
      createdAt: subDays(new Date(), 2),
      notes: 'USD payment from US customer',
      hasPromise: false,
    },
    {
      id: 201,
      receiptNumber: 'RCP-EUR-001',
      customerId: 201,
      customerName: 'European Solutions GmbH',
      customerAccountNumber: 'ACC-201',
      receiptDate: subDays(new Date(), 1),
      receivedAmount: 12000.0,
      allocatedAmount: 0,
      unappliedAmount: 12000.0,
      currency: 'EUR',
      homeReceivedAmount: 10260.0, // EUR 12000 × 0.855
      paymentMethod: 'bank_transfer',
      paymentReference: 'SEPA-EUR-001',
      bankReference: 'SEPA-REF-001',
      externalId: 'EXT-201',
      importedAt: subDays(new Date(), 1),
      createdBy: 'system',
      createdAt: subDays(new Date(), 1),
      notes: 'EUR payment from German customer',
      hasPromise: false,
    },
    {
      id: 127,
      receiptNumber: 'RCP-027',
      customerId: 127,
      customerName: 'Cloud Services Ltd',
      customerAccountNumber: 'ACC-027',
      receiptDate: new Date(),
      receivedAmount: 7800.0,
      allocatedAmount: 7800.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-20002',
      externalId: 'EXT-028',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 128,
      receiptNumber: 'RCP-028',
      customerId: 128,
      customerName: 'Software Development Inc',
      customerAccountNumber: 'ACC-028',
      receiptDate: new Date(),
      receivedAmount: 12500.0,
      allocatedAmount: 12500.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BT-20003',
      externalId: 'EXT-029',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 129,
      receiptNumber: 'RCP-029',
      customerId: 129,
      customerName: 'Consulting Group',
      customerAccountNumber: 'ACC-029',
      receiptDate: new Date(),
      receivedAmount: 9200.0,
      allocatedAmount: 9200.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'direct_debit',
      paymentReference: 'DD-20004',
      externalId: 'EXT-030',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 130,
      receiptNumber: 'RCP-030',
      customerId: 130,
      customerName: 'E-commerce Solutions',
      customerAccountNumber: 'ACC-030',
      receiptDate: new Date(),
      receivedAmount: 5600.0,
      allocatedAmount: 5600.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-20005',
      externalId: 'EXT-031',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 131,
      receiptNumber: 'RCP-031',
      customerId: 131,
      customerName: 'Data Analytics Corp',
      customerAccountNumber: 'ACC-031',
      receiptDate: new Date(),
      receivedAmount: 8900.0,
      allocatedAmount: 8900.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BT-20006',
      externalId: 'EXT-032',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 132,
      receiptNumber: 'RCP-032',
      customerId: 132,
      customerName: 'Mobile Apps Ltd',
      customerAccountNumber: 'ACC-032',
      receiptDate: new Date(),
      receivedAmount: 6300.0,
      allocatedAmount: 6300.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-20007',
      externalId: 'EXT-033',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 133,
      receiptNumber: 'RCP-033',
      customerId: 133,
      customerName: 'Web Design Studio',
      customerAccountNumber: 'ACC-033',
      receiptDate: new Date(),
      receivedAmount: 4200.0,
      allocatedAmount: 4200.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BT-20008',
      externalId: 'EXT-034',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 134,
      receiptNumber: 'RCP-034',
      customerId: 134,
      customerName: 'IT Infrastructure Inc',
      customerAccountNumber: 'ACC-034',
      receiptDate: new Date(),
      receivedAmount: 11200.0,
      allocatedAmount: 11200.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'direct_debit',
      paymentReference: 'DD-20009',
      externalId: 'EXT-035',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 135,
      receiptNumber: 'RCP-035',
      customerId: 135,
      customerName: 'Security Systems Co',
      customerAccountNumber: 'ACC-035',
      receiptDate: new Date(),
      receivedAmount: 7500.0,
      allocatedAmount: 7500.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-20010',
      externalId: 'EXT-036',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 136,
      receiptNumber: 'RCP-036',
      customerId: 136,
      customerName: 'Network Solutions Ltd',
      customerAccountNumber: 'ACC-036',
      receiptDate: new Date(),
      receivedAmount: 9800.0,
      allocatedAmount: 9800.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BT-20011',
      externalId: 'EXT-037',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 137,
      receiptNumber: 'RCP-037',
      customerId: 137,
      customerName: 'Business Intelligence Corp',
      customerAccountNumber: 'ACC-037',
      receiptDate: new Date(),
      receivedAmount: 6700.0,
      allocatedAmount: 6700.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-20012',
      externalId: 'EXT-038',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 138,
      receiptNumber: 'RCP-038',
      customerId: 138,
      customerName: 'AI Technologies Inc',
      customerAccountNumber: 'ACC-038',
      receiptDate: new Date(),
      receivedAmount: 13500.0,
      allocatedAmount: 13500.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BT-20013',
      externalId: 'EXT-039',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 139,
      receiptNumber: 'RCP-039',
      customerId: 139,
      customerName: 'Blockchain Solutions',
      customerAccountNumber: 'ACC-039',
      receiptDate: new Date(),
      receivedAmount: 8400.0,
      allocatedAmount: 8400.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'direct_debit',
      paymentReference: 'DD-20014',
      externalId: 'EXT-040',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 140,
      receiptNumber: 'RCP-040',
      customerId: 140,
      customerName: 'IoT Systems Ltd',
      customerAccountNumber: 'ACC-040',
      receiptDate: new Date(),
      receivedAmount: 5900.0,
      allocatedAmount: 5900.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-20015',
      externalId: 'EXT-041',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 141,
      receiptNumber: 'RCP-041',
      customerId: 141,
      customerName: 'Robotics Engineering',
      customerAccountNumber: 'ACC-041',
      receiptDate: new Date(),
      receivedAmount: 10500.0,
      allocatedAmount: 10500.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BT-20016',
      externalId: 'EXT-042',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 142,
      receiptNumber: 'RCP-042',
      customerId: 142,
      customerName: 'Automation Systems',
      customerAccountNumber: 'ACC-042',
      receiptDate: new Date(),
      receivedAmount: 7200.0,
      allocatedAmount: 7200.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-20017',
      externalId: 'EXT-043',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 143,
      receiptNumber: 'RCP-043',
      customerId: 143,
      customerName: 'Virtual Reality Co',
      customerAccountNumber: 'ACC-043',
      receiptDate: new Date(),
      receivedAmount: 9600.0,
      allocatedAmount: 9600.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'bank_transfer',
      paymentReference: 'BT-20018',
      externalId: 'EXT-044',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 144,
      receiptNumber: 'RCP-044',
      customerId: 144,
      customerName: 'Augmented Reality Ltd',
      customerAccountNumber: 'ACC-044',
      receiptDate: new Date(),
      receivedAmount: 6800.0,
      allocatedAmount: 6800.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'direct_debit',
      paymentReference: 'DD-20019',
      externalId: 'EXT-045',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
    {
      id: 145,
      receiptNumber: 'RCP-045',
      customerId: 145,
      customerName: 'Machine Learning Inc',
      customerAccountNumber: 'ACC-045',
      receiptDate: new Date(),
      receivedAmount: 11800.0,
      allocatedAmount: 11800.0,
      unappliedAmount: 0,
      currency: 'GBP',
      paymentMethod: 'credit_card',
      paymentReference: 'CC-20020',
      externalId: 'EXT-046',
      importedAt: new Date(),
      createdBy: 'system',
      createdAt: new Date(),
      hasPromise: false,
    },
  ];
  return receipts.map((x) => ({
    ...x,
    creditControllerId: `user-${String(((x.customerId - 1) % 6) + 1).padStart(3, '0')}`,
    allocationStatus: computeAllocationStatus(x.unappliedAmount, x.receivedAmount),
    onDispute: x.id % 11 === 0,
    hasDispute: x.id % 13 === 0,
  }));
};

// Mock Transactions for Allocation
export const generateMockTransactions = (customerId: number): Transaction[] => {
  const transactions: Record<number, Transaction[]> = {
    // TEST TRANSACTIONS FOR PROMISE TO PAY (Phase 2)
    1: [
      {
        id: 1,
        transactionNumber: 'INV-2024-001',
        transactionType: 'invoice',
        description: 'Consultancy Services – Jan 2024',
        customerId: 1,
        customerName: 'ABC Company Ltd',
        transactionDate: new Date('2024-12-01'),
        dueDate: new Date('2024-12-31'),
        originalAmount: 500.0,
        currentBalance: 500.0,
        currency: 'GBP',
        daysOverdue: 15,
        status: computeTransactionStatus(15, 500.0),
        onDispute: false,
        hasDispute: false,
        promisedAmount: 500.0,
        isPromised: true,
      },
      {
        id: 2,
        transactionNumber: 'INV-2024-002',
        transactionType: 'invoice',
        description: 'Software Licence Renewal',
        customerId: 1,
        customerName: 'ABC Company Ltd',
        transactionDate: new Date('2024-12-05'),
        dueDate: new Date('2025-01-04'),
        originalAmount: 350.0,
        currentBalance: 350.0,
        currency: 'GBP',
        daysOverdue: 11,
        status: computeTransactionStatus(11, 350.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 3,
        transactionNumber: 'INV-2024-003',
        transactionType: 'invoice',
        description: 'Hardware Supply Q4',
        customerId: 1,
        customerName: 'ABC Company Ltd',
        transactionDate: new Date('2024-12-10'),
        dueDate: new Date('2025-01-09'),
        originalAmount: 450.0,
        currentBalance: 450.0,
        currency: 'GBP',
        daysOverdue: 6,
        status: computeTransactionStatus(6, 450.0),
        onDispute: false,
        hasDispute: false,
        promisedAmount: 450.0,
        isPromised: true,
      },
      {
        id: 4,
        transactionNumber: 'INV-2024-004',
        transactionType: 'invoice',
        description: 'Support & Maintenance Dec',
        customerId: 1,
        customerName: 'ABC Company Ltd',
        transactionDate: new Date('2024-12-15'),
        dueDate: new Date('2025-01-14'),
        originalAmount: 275.0,
        currentBalance: 275.0,
        currency: 'GBP',
        daysOverdue: 1,
        status: computeTransactionStatus(1, 275.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 5,
        transactionNumber: 'INV-2024-005',
        transactionType: 'invoice',
        description: 'Professional Services',
        customerId: 1,
        customerName: 'ABC Company Ltd',
        transactionDate: new Date('2024-12-20'),
        dueDate: new Date('2025-01-19'),
        originalAmount: 300.0,
        currentBalance: 300.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 300.0),
        onDispute: false,
        hasDispute: false,
        promisedAmount: 300.0,
        isPromised: true,
      },
      {
        id: 6,
        transactionNumber: 'INV-2024-006',
        transactionType: 'invoice',
        description: 'Project Delivery Phase 2',
        customerId: 1,
        customerName: 'ABC Company Ltd',
        transactionDate: new Date('2024-12-25'),
        dueDate: new Date('2025-01-24'),
        originalAmount: 425.0,
        currentBalance: 425.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 425.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 7,
        transactionNumber: 'CN-2024-001',
        transactionType: 'credit_note',
        description: 'Credit Note – Returned Goods',
        customerId: 1,
        customerName: 'ABC Company Ltd',
        transactionDate: new Date('2024-12-18'),
        dueDate: new Date('2025-01-17'),
        originalAmount: -150.0,
        currentBalance: -150.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, -150.0),
        onDispute: false,
        hasDispute: false,
      },
    ],
    // TRANSACTIONS FOR ACME DISTRIBUTION LTD (REC-001 Allocation)
    101: [
      {
        id: 1001,
        transactionNumber: 'INV-2547',
        transactionType: 'invoice',
        description: 'Bulk Goods Supply – Sep',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-09-15'),
        dueDate: new Date('2025-10-15'),
        originalAmount: 2100.0,
        currentBalance: 2100.0,
        currency: 'GBP',
        daysOverdue: 26,
        status: computeTransactionStatus(26, 2100.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1002,
        transactionNumber: 'INV-2501',
        transactionType: 'invoice',
        description: 'Warehouse Storage Fees',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-09-22'),
        dueDate: new Date('2025-10-22'),
        originalAmount: 1850.0,
        currentBalance: 1850.0,
        currency: 'GBP',
        daysOverdue: 19,
        status: computeTransactionStatus(19, 1850.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1003,
        transactionNumber: 'INV-2465',
        transactionType: 'invoice',
        description: 'Logistics & Freight Oct',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-09-28'),
        dueDate: new Date('2025-10-28'),
        originalAmount: 950.0,
        currentBalance: 950.0,
        currency: 'GBP',
        daysOverdue: 13,
        status: computeTransactionStatus(13, 950.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1004,
        transactionNumber: 'INV-2420',
        transactionType: 'invoice',
        description: 'Distribution Services Q4',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-10-05'),
        dueDate: new Date('2025-11-04'),
        originalAmount: 3200.0,
        currentBalance: 3200.0,
        currency: 'GBP',
        daysOverdue: 6,
        status: computeTransactionStatus(6, 3200.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1005,
        transactionNumber: 'INV-2380',
        transactionType: 'invoice',
        description: 'Packing & Handling Charges',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-10-12'),
        dueDate: new Date('2025-11-11'),
        originalAmount: 1450.0,
        currentBalance: 1450.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 1450.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1006,
        transactionNumber: 'INV-2340',
        transactionType: 'invoice',
        description: 'Import Duty Recharge',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-10-18'),
        dueDate: new Date('2025-11-17'),
        originalAmount: 2750.0,
        currentBalance: 2750.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 2750.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1007,
        transactionNumber: 'INV-2300',
        transactionType: 'invoice',
        description: 'Fleet Management Oct',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-10-25'),
        dueDate: new Date('2025-11-24'),
        originalAmount: 1200.0,
        currentBalance: 1200.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 1200.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1008,
        transactionNumber: 'INV-2260',
        transactionType: 'invoice',
        description: 'Cold Storage Rental Nov',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-11-01'),
        dueDate: new Date('2025-12-01'),
        originalAmount: 3850.0,
        currentBalance: 3850.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 3850.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1009,
        transactionNumber: 'INV-2220',
        transactionType: 'invoice',
        description: 'Last Mile Delivery Nov',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-11-08'),
        dueDate: new Date('2025-12-08'),
        originalAmount: 875.0,
        currentBalance: 875.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 875.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1010,
        transactionNumber: 'INV-2180',
        transactionType: 'invoice',
        description: 'Freight Forwarding Nov',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-11-15'),
        dueDate: new Date('2025-12-15'),
        originalAmount: 2100.0,
        currentBalance: 2100.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 2100.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1011,
        transactionNumber: 'INV-2140',
        transactionType: 'invoice',
        description: 'Customs Clearance Nov',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-11-22'),
        dueDate: new Date('2025-12-22'),
        originalAmount: 1650.0,
        currentBalance: 1650.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 1650.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1012,
        transactionNumber: 'INV-2100',
        transactionType: 'invoice',
        description: 'Year-End Bulk Order',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-11-29'),
        dueDate: new Date('2025-12-29'),
        originalAmount: 3200.0,
        currentBalance: 3200.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 3200.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1013,
        transactionNumber: 'CN-1001',
        transactionType: 'credit_note',
        description: 'Credit Note – Damaged Goods Return',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-10-20'),
        dueDate: new Date('2025-11-19'),
        originalAmount: -450.0,
        currentBalance: -450.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, -450.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1014,
        transactionNumber: 'DA-1001',
        transactionType: 'debit_adjustment',
        description: 'Debit Adjustment – Late Payment Fee',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-11-05'),
        dueDate: new Date('2025-12-05'),
        originalAmount: 125.0,
        currentBalance: 125.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 125.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 1015,
        transactionNumber: 'CA-1001',
        transactionType: 'credit_adjustment',
        description: 'Credit Adjustment – Early Payment Discount',
        customerId: 101,
        customerName: 'Acme Distribution Ltd',
        transactionDate: new Date('2025-11-10'),
        dueDate: new Date('2025-12-10'),
        originalAmount: -200.0,
        currentBalance: -200.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, -200.0),
        onDispute: false,
        hasDispute: false,
      },
    ],
    102: [
      {
        id: 2001,
        transactionNumber: 'INV-2001',
        transactionType: 'invoice',
        description: 'IT Infrastructure Services',
        customerId: 102,
        customerName: 'Tech Solutions Inc',
        transactionDate: subDays(new Date(), 50),
        dueDate: subDays(new Date(), 20),
        originalAmount: 2500.0,
        currentBalance: 1200.0,
        currency: 'GBP',
        daysOverdue: 20,
        status: computeTransactionStatus(20, 1200.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 2002,
        transactionNumber: 'INV-2010',
        transactionType: 'invoice',
        description: 'Cloud Hosting Annual Fee',
        customerId: 102,
        customerName: 'Tech Solutions Inc',
        transactionDate: subDays(new Date(), 30),
        dueDate: new Date(),
        originalAmount: 1800.0,
        currentBalance: 1800.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 1800.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 2003,
        transactionNumber: 'INV-2015',
        transactionType: 'invoice',
        description: 'Software Development Sprint',
        customerId: 102,
        customerName: 'Tech Solutions Inc',
        transactionDate: subDays(new Date(), 20),
        dueDate: addDays(new Date(), 10),
        originalAmount: 2200.0,
        currentBalance: 2200.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 2200.0),
        onDispute: false,
        hasDispute: false,
      },
    ],
    104: [
      {
        id: 4001,
        transactionNumber: 'INV-4001',
        transactionType: 'invoice',
        description: 'Steel Components Batch A',
        customerId: 104,
        customerName: 'Manufacturing Co Ltd',
        transactionDate: subDays(new Date(), 60),
        dueDate: subDays(new Date(), 30),
        originalAmount: 5000.0,
        currentBalance: 5000.0,
        currency: 'GBP',
        daysOverdue: 30,
        status: computeTransactionStatus(30, 5000.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 4002,
        transactionNumber: 'INV-4010',
        transactionType: 'invoice',
        description: 'Assembly Line Tooling',
        customerId: 104,
        customerName: 'Manufacturing Co Ltd',
        transactionDate: subDays(new Date(), 45),
        dueDate: subDays(new Date(), 15),
        originalAmount: 4500.0,
        currentBalance: 4500.0,
        currency: 'GBP',
        daysOverdue: 15,
        status: computeTransactionStatus(15, 4500.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 4003,
        transactionNumber: 'INV-4020',
        transactionType: 'invoice',
        description: 'Quality Control Services',
        customerId: 104,
        customerName: 'Manufacturing Co Ltd',
        transactionDate: subDays(new Date(), 30),
        dueDate: new Date(),
        originalAmount: 3000.0,
        currentBalance: 3000.0,
        currency: 'GBP',
        daysOverdue: 0,
        status: computeTransactionStatus(0, 3000.0),
        onDispute: false,
        hasDispute: false,
      },
    ],

    // INVOICES FOR EUROPEAN SOLUTIONS GMBH (RCP-EUR-001 Allocation)
    201: [
      {
        id: 20101,
        transactionNumber: 'INV-EUR-2401',
        transactionType: 'invoice',
        description: 'European Consulting Services',
        customerId: 201,
        customerName: 'European Solutions GmbH',
        transactionDate: subDays(new Date(), 65),
        dueDate: subDays(new Date(), 35),
        originalAmount: 4200.0,
        currentBalance: 4200.0,
        currency: 'EUR',
        homeCurrencyAmount: 3591.0, // EUR × 0.855
        homeCurrencyBalance: 3591.0,
        daysOverdue: 35,
        status: computeTransactionStatus(35, 4200.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 20102,
        transactionNumber: 'INV-EUR-2402',
        transactionType: 'invoice',
        description: 'Software Localisation',
        customerId: 201,
        customerName: 'European Solutions GmbH',
        transactionDate: subDays(new Date(), 52),
        dueDate: subDays(new Date(), 22),
        originalAmount: 3750.0,
        currentBalance: 3750.0,
        currency: 'EUR',
        homeCurrencyAmount: 3206.25,
        homeCurrencyBalance: 3206.25,
        daysOverdue: 22,
        status: computeTransactionStatus(22, 3750.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 20103,
        transactionNumber: 'INV-EUR-2403',
        transactionType: 'invoice',
        description: 'GDPR Compliance Audit',
        customerId: 201,
        customerName: 'European Solutions GmbH',
        transactionDate: subDays(new Date(), 40),
        dueDate: subDays(new Date(), 10),
        originalAmount: 2800.0,
        currentBalance: 2800.0,
        currency: 'EUR',
        homeCurrencyAmount: 2394.0,
        homeCurrencyBalance: 2394.0,
        daysOverdue: 10,
        status: computeTransactionStatus(10, 2800.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 20104,
        transactionNumber: 'INV-EUR-2404',
        transactionType: 'invoice',
        description: 'Market Expansion Support',
        customerId: 201,
        customerName: 'European Solutions GmbH',
        transactionDate: subDays(new Date(), 25),
        dueDate: addDays(new Date(), 5),
        originalAmount: 1600.0,
        currentBalance: 1600.0,
        currency: 'EUR',
        homeCurrencyAmount: 1368.0,
        homeCurrencyBalance: 1368.0,
        daysOverdue: 0,
        status: computeTransactionStatus(0, 1600.0),
        onDispute: false,
        hasDispute: false,
      },
      {
        id: 20105,
        transactionNumber: 'INV-EUR-2405',
        transactionType: 'invoice',
        description: 'Partner Integration Project',
        customerId: 201,
        customerName: 'European Solutions GmbH',
        transactionDate: subDays(new Date(), 12),
        dueDate: addDays(new Date(), 18),
        originalAmount: 2250.0,
        currentBalance: 2250.0,
        currency: 'EUR',
        homeCurrencyAmount: 1923.75,
        homeCurrencyBalance: 1923.75,
        daysOverdue: 0,
        status: computeTransactionStatus(0, 2250.0),
        onDispute: false,
        hasDispute: false,
      },
    ],
  };

  return transactions[customerId] || [];
};
